import { Alert, Typography } from '@mui/material';
import Stack from '@mui/system/Stack';
import React from 'react';
import { Outlet } from 'react-router-dom';
import { useAuthContext } from '../context/auth';
import {
	PROTECTED_ROUTE_ERROR,
	PROTECTED_ROUTE_ERROR_TITLE,
	PROTECTED_ROUTE_PERMISSIONS_REQUIRED_TITLE,
} from '../data/constants';
import { Permission } from '../permissions';

interface ProtectedRouteProps {
	/**
	 * The permission[s] required to access the route.
	 */
	permission: Permission | Permission[];
	/**
	 * A secondary permission that can be used to bypass the primary permission.
	 */
	bypassPermission?: Permission;
}

/**
 * A route that is only accessible to users with the specified permission.
 * @returns
 * @example
 * 									
	<ProtectedRoute
    permission='policyholder.fetchInvoices'
    bypassPermission='*.fetch'
  >
    <InvoicesView />
  </ProtectedRoute>
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
	permission,
	bypassPermission,
	children,
}) => {
	const { hasPermission } = useAuthContext();
	const isAllowed = Array.isArray(permission)
		? permission.every((permission) => hasPermission(permission))
		: hasPermission(permission) || hasPermission(bypassPermission);
	if (!isAllowed) {
		return (
			<Alert severity='error' sx={{ mt: 2 }}>
				<Stack spacing={1} direction='column'>
					<Typography fontWeight={500}>
						{PROTECTED_ROUTE_ERROR_TITLE}
					</Typography>
					<Typography variant='body2'>{PROTECTED_ROUTE_ERROR}</Typography>
					<Typography variant='body2'>
						{PROTECTED_ROUTE_PERMISSIONS_REQUIRED_TITLE}
						{Array.isArray(permission) ? permission.join(', ') : permission}
					</Typography>
				</Stack>
			</Alert>
		);
	}

	return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;
